import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { API_URL, TENANT_ID } from "../../config";
import { getMessage } from "../../data/locales";
import { clearSessionKeys } from "../../utils/sessionKeys";
import AuthScreenLayout from "../../components/AuthScreenLayout";
import "./VerifyEmail.css";

const MIN_TOKEN_LENGTH = 10;

const VerifyEmail = () => {
  const { token: tokenFromUrl } = useParams();
  const token = (tokenFromUrl || "").trim();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [message, setMessage] = useState("");
  const [pendingOrderId, setPendingOrderId] = useState(null);
  const appliedForToken = React.useRef(null);

  useEffect(() => {
    const currentToken = token;

    if (!currentToken || currentToken.length < MIN_TOKEN_LENGTH) {
      setStatus("error");
      setMessage(getMessage("verifyEmail.incompleteLink"));
      return undefined;
    }

    const controller = new AbortController();

    const verifyToken = async () => {
      console.log(`[VerifyEmail] Starting verification for token length: ${currentToken.length}`);

      try {
        const response = await fetch(`${API_URL}/verify-email`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "x-tenant-id": TENANT_ID,
          },
          body: JSON.stringify({ token: currentToken }),
          signal: controller.signal,
        });

        const data = await response.json();

        // Ignore a second response for the same token (for example a duplicate request).
        if (appliedForToken.current === currentToken) return;
        appliedForToken.current = currentToken;

        if (response.ok) {
          clearSessionKeys();
          if (data.setupRequired && data.setupToken) {
            const setupParams = new URLSearchParams({ token: data.setupToken });
            if (data.pendingOrderId) {
              setupParams.set("orderId", data.pendingOrderId);
            }
            navigate(`/setup-account?${setupParams.toString()}`, {
              replace: true,
            });
            return;
          }
          if (data.pendingOrderId) {
            console.log(`[VerifyEmail] Pending Order ID from backend: ${data.pendingOrderId}`);
            setPendingOrderId(data.pendingOrderId);
          }
          setStatus("success");
        } else {
          console.error(`[VerifyEmail] Verification failed: ${data.error}`);
          setStatus("error");
          setMessage(
            typeof data.error === "string" && data.error.trim()
              ? data.error
              : getMessage("verifyEmail.invalidDefault"),
          );
        }
      } catch (error) {
        if (error?.name === "AbortError") return;
        console.error(`[VerifyEmail] Network error:`, error);
        if (appliedForToken.current === currentToken) return;
        appliedForToken.current = currentToken;
        setStatus("error");
        setMessage(getMessage("verifyEmail.connectionError"));
      }
    };

    verifyToken();
    return () => controller.abort();
  }, [navigate, token]);

  const handleSuccessNavigation = () => {
    if (pendingOrderId) {
      navigate(`/checkout?orderId=${pendingOrderId}`);
    } else {
      navigate("/login");
    }
  };

  return (
    <AuthScreenLayout>
      <div className="auth-screen-card">
        {status === "verifying" && (
          <div className="auth-screen-status" role="status">
            <div className="auth-screen-spinner" aria-hidden="true" />
            <h2>{getMessage("verifyEmail.checkingTitle")}</h2>
            <p>{getMessage("verifyEmail.checkingBody")}</p>
          </div>
        )}

        {status === "success" && (
          <>
            <h2>{getMessage("verifyEmail.successTitle")}</h2>
            <div className="auth-screen-message success">
              {pendingOrderId
                ? getMessage("verifyEmail.successBodyPending")
                : getMessage("verifyEmail.successBody")}
            </div>
            <button type="button" className="auth-screen-button" onClick={handleSuccessNavigation}>
              {pendingOrderId
                ? getMessage("verifyEmail.continuePayment")
                : getMessage("resetPassword.goToLogin")}
            </button>
          </>
        )}

        {status === "error" && (
          <>
            <h2>{getMessage("verifyEmail.invalidTitle")}</h2>
            <div className="auth-screen-message error" role="alert">
              {message}
            </div>
            <p className="verify-email-note">{getMessage("verifyEmail.expiryNote")}</p>
            <div className="auth-screen-links">
              <Link to="/login">{getMessage("resetPassword.goToLogin")}</Link>
              <Link to="/NewRegistro">{getMessage("legal.backToRegister")}</Link>
            </div>
          </>
        )}
      </div>
    </AuthScreenLayout>
  );
};

export default VerifyEmail;
